import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { notifications } from "@mantine/notifications";
import { mappingApi } from "@services/mappingApi";
import { ApiError } from "@utils/httpClient";
import {
  triggerJsonDownload,
  triggerUrlDownload,
} from "@utils/jobResultsHelpers";
import type { JobViewModel } from "@models/job";
import type { UseJobResultsState } from "@hooks/useJobResults.types";

export interface UseResultsExportResult {
  exportingExcel: boolean;
  handleExport: () => void;
  handleExportExcel: () => Promise<void>;
}

/**
 * Export handlers: a client-side JSON snapshot of the current review state,
 * and a server-generated Excel download.
 */
export const useResultsExport = (
  job: JobViewModel | null,
  results: UseJobResultsState,
): UseResultsExportResult => {
  const { t } = useTranslation();
  const [exportingExcel, setExportingExcel] = useState(false);

  const handleExport = useCallback((): void => {
    if (!job) return;
    const payload = {
      jobId: job.jobId,
      filename: job.filename,
      exportedAt: new Date().toISOString(),
      rows: results.rows.map((row) => {
        const edit = results.edits.get(row.rowIndex);
        return {
          rowIndex: row.rowIndex,
          kind: row.kind,
          source: row.source,
          reviewStatus: edit?.reviewStatus ?? row.reviewStatus,
          chosenRank: edit?.chosenRank,
          original: {
            reviewStatus: row.reviewStatus,
            mappingStatus: row.mappingStatus,
            // The candidate the row points at. The rest of them are their own
            // endpoint and are not in memory unless a dropdown was opened.
            target: row.target,
          },
        };
      }),
    };
    triggerJsonDownload(`${job.jobId}-review.json`, payload);
  }, [job, results]);

  const handleExportExcel = useCallback(async (): Promise<void> => {
    if (!job) return;
    setExportingExcel(true);
    try {
      const result = await mappingApi.exportJobResult(job.jobId);
      if (!result.downloadUrl || result.rowCount === 0) {
        notifications.show({
          color: "yellow",
          title: t("jobResultsPage.export.noDataTitle", {
            defaultValue: "Nothing to export",
          }),
          message: t("jobResultsPage.export.noData", {
            defaultValue: "There are no mapping results to export.",
          }),
          withCloseButton: true,
        });
        return;
      }
      triggerUrlDownload(result.downloadUrl, result.filename);
    } catch (err) {
      notifications.show({
        color: "red",
        title: t("jobResultsPage.export.failedTitle", {
          defaultValue: "Export failed",
        }),
        message:
          err instanceof ApiError
            ? err.message
            : err instanceof Error
              ? err.message
              : t("jobResultsPage.export.failed", {
                  defaultValue: "Could not generate the Excel file.",
                }),
        withCloseButton: true,
      });
    } finally {
      setExportingExcel(false);
    }
  }, [job, t]);

  return { exportingExcel, handleExport, handleExportExcel };
};
